import { Effect } from 'effect'

type IntercomQueueItem = [method: string, ...args: unknown[]]

declare global {
  interface Window {
    Intercom: ((...args: unknown[]) => void) & {
      q?: IntercomQueueItem[]
    }
    intercomSettings: Record<string, unknown>
  }
}

let loaderInjected = false
let isBooted = false

function createIntercomProxy(intercomAppId: string) {
  const w = window as typeof window & { attachEvent?: typeof window.addEventListener }
  const existing = w.Intercom

  if (typeof existing === 'function') {
    existing('reattach_activator')
    existing('update', w.intercomSettings)
    return
  }

  const queue: IntercomQueueItem[] = []
  const proxy = function IntercomProxy(method: string, ...args: unknown[]) {
    queue.push([method, ...args])
  } as typeof window.Intercom

  proxy.q = queue

  w.Intercom = proxy

  const load = () => {
    if (loaderInjected) return
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = `https://widget.intercom.io/widget/${intercomAppId}`
    const x = document.getElementsByTagName('script')[0]
    x.parentNode?.insertBefore(script, x)
    loaderInjected = true
  }

  if (document.readyState === 'complete') {
    load()
  } else if (w.attachEvent) {
    w.attachEvent('onload', load)
  } else {
    w.addEventListener('load', load, false)
  }
}

function ensureIntercom(intercomAppId: string) {
  if (typeof window === 'undefined') return

  createIntercomProxy(intercomAppId)

  if (!isBooted) {
    window.Intercom('boot', {
      app_id: intercomAppId
    })
    isBooted = true
  }
}

/** The only seam that calls the foreign Intercom browser API. */
export function callIntercom(intercomAppId: string, method: string, ...args: unknown[]) {
  return Effect.sync(() => {
    if (typeof window == 'undefined') return
    ensureIntercom(intercomAppId)
    window.Intercom(method, ...args)
  })
}
